import React, { useEffect, useRef, useState } from 'react';
import * as AdaptiveCards from 'adaptivecards';
import 'adaptivecards/dist/adaptivecards.css';
import { SubmitAction } from 'adaptivecards';
import './AdaptiveCustomStyles.css';

import { marked } from 'marked';
import { BotResponse } from '../../api';
import { exportOptions } from '../../utils/pptxExportHandlers';
import { CommandBarButton, Stack } from '@fluentui/react';
import { CheckmarkRegular, Copy20Filled } from '@fluentui/react-icons'
import * as ReactDOM from 'react-dom/client';

interface AdaptiveCardRendererProps {
  card: any;
  onSubmit: (data: any) => void;
  isLoading: boolean;
  messages?: BotResponse[];
  messageId?: string;
}

const AdaptiveCardRenderer: React.FC<AdaptiveCardRendererProps> = ({ card, onSubmit, isLoading, messages, messageId }) => {
  const [isBlinking, setIsBlinking] = useState(false);

  const cardContainer = useRef<HTMLDivElement>(null);
  const inputValues = useRef<{ [key: string]: string }>({});
  const [showcopiedStatus, setshowcopiedStatus] = useState(false)

  // Function to extract values from HTML inputs and selects
  const extractInputValues = (container: HTMLElement) => {
    const inputs = container.querySelectorAll('input, select');
    const values: { [key: string]: string } = {};
    inputs.forEach(input => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
        const key = `${input.tagName}-${input.name}`;
        values[key] = input.value;
      }
    });
    return values;
  };

  function changeCSSTest(
    renderedCard: HTMLElement,
    matchText: string,
    originalFlex: string,
    newFlex: string
  ) {
    if (renderedCard.innerText.includes(matchText)) {
      const elements = renderedCard.querySelectorAll(`[style*="flex: ${originalFlex}"]`);
      elements.forEach(el => {
        (el as HTMLElement).style.flex = newFlex;
      });
    }
  }
  // Function to map values to new rendered object
  const mapValuesToCard = (renderedCard: HTMLElement, values: { [key: string]: string }) => {
    changeCSSTest(renderedCard, "OTP Statistics by Category", "1 1 25%", "1 1 80%");
    const inputs = renderedCard.querySelectorAll('input, select');
    inputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        const key = `${input.tagName}-${input.name}`;
        if (values[key]) {
          input.value = values[key];
        }
      } else if (input instanceof HTMLSelectElement) {
        const key = `${input.tagName}-${input.name}`;
        const options = input.querySelectorAll('option');
        options.forEach(option => {
          if (option.value === values[key]) {
            option.selected = true;
          }
        });
      }
    });
  };

  // Function to change state of buttons regarding to isLoading state (disable when loading data from api / agent)
  const changeButtonsState = (renderedCard: HTMLElement) => {
    const buttons = renderedCard.querySelectorAll<HTMLButtonElement>('button');
    buttons.forEach((button) => {
      button.disabled = isLoading;
    })
  };

  // Function to disable all inputs, selects, and buttons with specific classes, except the last one
  const disableInputsExceptLast = (): void => {
    const containers = document.querySelectorAll('[class^="_chatMessageStream_"] [class^="_chatMessageGpt_"]');
    const containersExceptLast = Array.from(containers).slice(0, containers.length - 1);

    containersExceptLast.forEach(container => {
      const elements = Array.from(container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>('input, select, button'));

      const dropdown = container.querySelector<HTMLSelectElement>('select');
      const inputs = container.querySelectorAll<HTMLInputElement>('input');

      if (dropdown || inputs.length > 0) {
        elements.forEach(element => {
          element.disabled = true;
        });
      }
    });
  };

  useEffect(() => {

    if (cardContainer.current) {
      const adaptiveCard = new AdaptiveCards.AdaptiveCard();

      AdaptiveCards.AdaptiveCard.onProcessMarkdown = (text, result) => {
        result.outputHtml = marked(text);
        result.didProcess = true;
      };

      if (card) {
        adaptiveCard.parse(card);

        // Handle ToggleVisibility action
        adaptiveCard.onExecuteAction = (action: AdaptiveCards.Action) => {
          if ((action as any).type === "Action.ToggleVisibility") {
            const toggleAction = action as any;
            toggleAction.targetElements.forEach((element: any) => {
              const targetElement = document.getElementById(element.elementId);
              if (targetElement) {
                targetElement.style.display = targetElement.style.display === 'none' ? 'block' : 'none';
              }
            });
          } else if ((action as any).type === "Action.OpenUrl") {
            window.open((action as any).url, '_blank');
          } else if (action instanceof SubmitAction) {
            const data = action.data;  // data sent with the submit action
            const newInputValues = extractInputValues(cardContainer.current as HTMLElement);
            inputValues.current = { ...inputValues.current, ...newInputValues };
            onSubmit(data);  // handle the submit data

            // Disable inputs and selects after submit
            disableInputsExceptLast();
          }
        };

        const renderedCard = adaptiveCard.render() as HTMLElement;

        if (cardContainer.current.firstChild) {
          const previousValues = extractInputValues(cardContainer.current.firstChild as HTMLElement);
          inputValues.current = { ...inputValues.current, ...previousValues };
        }

        cardContainer.current.innerHTML = ''; // Clear previous content
        cardContainer.current.appendChild(renderedCard);

        // Map values to new rendered object
        mapValuesToCard(renderedCard, inputValues.current);

        // Change buttons state when loading (or not) the data from api / agent
        changeButtonsState(renderedCard);

        // Disable inputs and selects for old cards, except the last one
        disableInputsExceptLast();

        if (shouldRenderCopyButton || (shouldRenderExportButton && messages && messages.length > 0)) {
          const actionsContainer = document.createElement("div");
          actionsContainer.style.display = "flex";
          actionsContainer.style.justifyContent = "flex-end";
          actionsContainer.style.marginTop = "10px";

          if (shouldRenderCopyButton) {
            const copyButtonRoot = document.createElement("div");
            const copyRoot = ReactDOM.createRoot(copyButtonRoot);
            copyRoot.render(<CopyButton />);
            actionsContainer.appendChild(copyButtonRoot);
          }

          if (shouldRenderExportButton && messages && messages.length > 0) {
            const exportButtonRoot = document.createElement("div");
            const exportRoot = ReactDOM.createRoot(exportButtonRoot);
            exportRoot.render(
              <CommandBarButton
                text="Export"
                menuProps={exportOptions(messages, { message_id: messageId }, setIsBlinking)}
                className={isBlinking ? "blinking" : ''}
              />
            );
            actionsContainer.appendChild(exportButtonRoot);
          }

          renderedCard.appendChild(actionsContainer);
        }
      }
    }


  }, [card, onSubmit]);


  
const CopyButton = () => {
  const [showCopiedStatus, setShowCopiedStatus] = useState(false);

  const onCopyMessageClicked = async () => {
    if (!messageId) return;

    // Sprawdzenie czy jest adaptiveCard z obrazkiem
    const imageCard = card?.body?.find(
      (item: any) => item.type === 'Image' && item.url?.startsWith('data:image')
    );

    if (imageCard) {
      try {
        const response = await fetch(imageCard.url);
        const blob = await response.blob();

        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob,
          }),
        ]);
      } catch (error) {
        console.warn('Clipboard image copy failed, fallback to download.', error);

        // Fallback: pobieranie obrazka jako plik
        const link = document.createElement('a');
        link.href = imageCard.url;
        link.download = 'copied-image.png'; // Możesz dynamicznie ustawić nazwę
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }

    const table = card?.body?.find((item: any) => item.type === "Table");
    if (table && Array.isArray(table.rows)) {
      //markdown format
      // const extractTextFromCell = (cell: any): string => {
      // if (!cell.items || !Array.isArray(cell.items)) return "";
      // return cell.items.map((item: any) => item.text || "").join(" ");
      // };

      // const rows = table.rows.map((row: any) =>
      //   row.cells.map(extractTextFromCell)
      // );

      // // Format as Markdown
      // const header = rows[0];
      // const separator = header.map(() => "---");
      // const markdownRows = [header, separator, ...rows.slice(1)];

      // const markdown = markdownRows.map(row => `| ${row.join(" | ")} |`).join("\n");

      // navigator.clipboard.writeText(markdown)
      //   .then(() => console.log("Table copied to clipboard"))
      //   .catch(err => console.error("Failed to copy table:", err));


      //TSV format
      
    const extractTextFromCell = (cell: any): string => {
        if (!cell.items || !Array.isArray(cell.items)) return "";
        return cell.items.map((item: any) => item.text || "").join(" ");
      };

      const rows = table.rows.map((row: any) =>
        row.cells.map(extractTextFromCell)
      );

      const tsv = rows.map((row: any) => row.join("\t")).join("\n");

      navigator.clipboard.writeText(tsv)
        .then(() => console.log("TSV table copied to clipboard"))
        .catch(err => console.error("Failed to copy TSV table:", err));
    }

    setShowCopiedStatus(true);
    setTimeout(() => setShowCopiedStatus(false), 2000);
  };

  return showCopiedStatus ? (
    <CheckmarkRegular
      style={{
        height: '20px',
        width: '20px',
        color: 'green',
        cursor: 'pointer',
      }}
    />
  ) : (
    <Copy20Filled
      onClick={onCopyMessageClicked}
      style={{
        color: 'slategray',
        cursor: 'pointer',
      }}
    />
  );
};

  // const onCopyMessageClicked = async () => {
  //   if (!messageId) return;

  //   // Sprawdzenie czy jest adaptiveCard z obrazkiem
  //   const imageCard = card?.body?.find(
  //     (item: any) => item.type === 'Image' && item.url?.startsWith('data:image')
  //   );

  //   if (imageCard) {
  //     try {
  //       const response = await fetch(imageCard.url);
  //       const blob = await response.blob();

  //       await navigator.clipboard.write([
  //         new ClipboardItem({
  //           [blob.type]: blob,
  //         }),
  //       ]);
  //     } catch (error) {
  //       console.warn('Clipboard image copy failed, fallback to download.', error);

  //       // Fallback: pobieranie obrazka jako plik
  //       const link = document.createElement('a');
  //       link.href = imageCard.url;
  //       link.download = 'copied-image.png'; // Możesz dynamicznie ustawić nazwę
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //     }
  //   }

  //   setshowcopiedStatus(true);
  //   setTimeout(() => setshowcopiedStatus(false), 2000);
  // };


  const hasDropdown = card?.body?.some(
    (item: any) => item.type === "Input.ChoiceSet" && item.style === "compact"
  );

  const hasSubmit = card?.body?.some(
    (item: any) =>
      item.type === "ActionSet" &&
      item.actions?.some((action: any) => action.type === "Action.Submit")
  );

  
  const hasImageCard = card?.body?.some(
    (item: any) => item.type === "Image" && item.url?.startsWith("data:image")
  );

  const hasTableCard = card?.body?.some(
    (item: any) => item.type === "Table" && Array.isArray(item.rows) && item.rows.length > 0
  );

  // Check if this is a greeting adaptive card (PGGA welcome tiles, etc.)
  const isGreetingAdaptiveCard = messages?.find(msg => 
    msg.id === messageId && 
    msg.type === 'adaptiveCard' && 
    msg.MessageCategory === 'greeting'
  ) !== undefined;

  const shouldRenderExportButton = !hasDropdown && !hasSubmit && !isGreetingAdaptiveCard;
  const shouldRenderCopyButton = !hasDropdown && !hasSubmit && (hasImageCard || hasTableCard);

  return (
    <div ref={cardContainer} className="ac-container" />
  );

};

export default AdaptiveCardRenderer;
